
import { AudioPlayer, AudioPlayerHandle } from "@/components/ui/audio-player";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { MultiVideoPlayer } from "@/components/ui/multi-video-player";
import { NoData } from "@/components/ui/no-data";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { VideoPlayer } from "@/components/ui/video-player";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ChatConversation } from "@/components/ui/chat-bubble";
import { CallLog, CallLogFilters, CallLogsResponse } from "@/models/CallLog";
import { callCostRows, parseCallCost, type CallCost } from "./callCost";
import { format } from "date-fns";
import { FileText, Phone, Play, RefreshCw, Download, Search, X, Copy, Check, Activity, Filter, ExternalLink, ChevronLeft, ChevronRight, Stethoscope } from "lucide-react";
import { DiagnoseSheet } from "./diagnose/DiagnoseSheet";
import { useCanDiagnose } from "@/hooks/useCanDiagnose";
import { callDiagnosisService } from "@/services/callDiagnosisService";
import { useEffect, useState, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { callLogService } from "@/services/callLogService";
import { doNotCallKey, doNotCallService, type DoNotCallListing } from "@/services/doNotCallService";
import { DoNotCallBadge } from "@/components/shared/DoNotCallBadge";
import { supabase } from "../../../supabase/supabase";
import { useOrganization } from "../../App";
import { isOnlyTrackingPhrases } from "@/utils/transcriptFiltering";
import { CALL_END_REASONS, CALL_LOG_STATUS } from "@/utils/constants";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LANGUAGE_CODES, languageName, type LanguageCode } from "@/config/language-rules";

interface CallLanguageSummary {
  start: LanguageCode;
  final: LanguageCode;
  switches: { turn: number; from: LanguageCode; to: LanguageCode; reason: "ask" | "follow" }[];
}

const isLanguageCode = (value: unknown): value is LanguageCode =>
  typeof value === "string" && (LANGUAGE_CODES as readonly string[]).includes(value);

// call_logs.language is jsonb written by the voice server. Any shape that is not as expected gives
// null (no Language line); a bad switch entry is skipped.
const readCallLanguage = (raw: unknown): CallLanguageSummary | null => {
  try {
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
    const value = raw as Record<string, unknown>;
    if (!isLanguageCode(value.start) || !isLanguageCode(value.final)) return null;
    const switches: CallLanguageSummary["switches"] = [];
    if (Array.isArray(value.switches)) {
      for (const entry of value.switches) {
        if (!entry || typeof entry !== "object") continue;
        const item = entry as Record<string, unknown>;
        if (
          typeof item.turn === "number" &&
          isLanguageCode(item.from) &&
          isLanguageCode(item.to) &&
          (item.reason === "ask" || item.reason === "follow")
        ) {
          switches.push({ turn: item.turn, from: item.from, to: item.to, reason: item.reason });
        }
      }
    }
    return { start: value.start, final: value.final, switches };
  } catch {
    return null;
  }
};

// call_logs.{stt,llm,tts}_config is jsonb `{ provider, model, ... }` written by the voice server.
// Gives "provider · model", whichever half is present, or null when neither is.
const formatModelLabel = (raw: unknown): string | null => {
  try {
    const value = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (!value || typeof value !== "object" || Array.isArray(value)) return null;
    const { provider, model } = value as Record<string, unknown>;
    const parts = [provider, model].filter((p): p is string => typeof p === "string" && p.trim() !== "");
    return parts.length ? parts.join(" · ") : null;
  } catch {
    return null;
  }
};

// Read the call-log filters from the URL, dropping unknown status / end reason values
const filtersFromParams = (searchParams: URLSearchParams) => {
  const status = searchParams.get("status") ?? "";
  const endedReason = searchParams.get("endedReason") ?? "";
  const validStatus =
    status &&
    status !== "all" &&
    Object.prototype.hasOwnProperty.call(CALL_LOG_STATUS, status);
  const validEndReason =
    endedReason &&
    endedReason !== "all" &&
    Object.prototype.hasOwnProperty.call(CALL_END_REASONS, endedReason);
  return {
    search: searchParams.get("search") ?? "",
    startDate: searchParams.get("startDate") ?? "",
    endDate: searchParams.get("endDate") ?? "",
    status: (validStatus ? status : "") as string,
    endedReason: (validEndReason ? endedReason : "") as string,
  };
};

interface CallLogsListProps {
  /** Only this assistant's calls. Filters then live in local state, not the URL, so the list can sit inside another page. */
  assistantId?: string;
}

// The search uses trigram indexes (migration 20261105120000), which need 3+ characters. A shorter
// term makes Postgres read every row of the organization: on prod 19 s for the page and 12 s for the
// count in an organization of 98k calls, against a few ms at 3 characters.
const MIN_SEARCH_LENGTH = 3;

/** The search term to send: trimmed, or "" (no search) while it is shorter than MIN_SEARCH_LENGTH */
const searchTerm = (input?: string | null) => {
  const term = (input ?? "").trim();
  return term.length >= MIN_SEARCH_LENGTH ? term : "";
};

// Base is the project URL, e.g. https://langfuse.voxi.us/project/voxi. Unset: no icon.
const langfuseTraceUrl = (traceId?: string | null) => {
  const base = import.meta.env.VITE_LANGFUSE_URL as string | undefined;
  return base && traceId ? `${base.replace(/\/+$/, "")}/traces/${traceId}` : null;
};

const CallLogsList = ({ assistantId }: CallLogsListProps = {}) => {
  const navigate = useNavigate();
  // Call costs are internal: only platform admins (superadmins) see the breakdown
  const { isPlatformAdmin, currentOrganization } = useOrganization();
  // The list is the current organization's calls (the one picked in the switcher). Without it the
  // query was "the newest calls I can see" with only RLS narrowing it: Postgres walked call_logs
  // newest-first through every organization's rows, and for a small organization on prod (3 calls
  // among 99k) read all of them: 25 s. With it, idx_call_logs_org_created serves it: 10 ms.
  const organizationId = currentOrganization?.id;
  const embedded = !!assistantId;
  const [searchParams, setSearchParams] = useSearchParams();
  const [callLogs, setCallLogs] = useState<CallLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Start from the URL so the URL->filters and filters->URL effects agree on
  // mount; starting empty made them overwrite each other in a fetch loop.
  const [filters, setFilters] = useState(() =>
    filtersFromParams(embedded ? new URLSearchParams() : searchParams)
  );
  const [searchInput, setSearchInput] = useState(filters.search);
  const [dateStartInput, setDateStartInput] = useState(filters.startDate);
  const [dateEndInput, setDateEndInput] = useState(filters.endDate);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [modalStatusFilter, setModalStatusFilter] = useState<string>("all");
  const [modalEndReasonFilter, setModalEndReasonFilter] = useState<string>("all");
  // Previous/Next pages over cursor (keyset) pagination: a page is "the calls older than its
  // cursor", which stays fast however deep you go. pageCursors[n] is page n's cursor (undefined for
  // the first page), recorded as Next is pressed, so Previous knows where it goes back to.
  const [pageCursors, setPageCursors] = useState<(string | undefined)[]>([undefined]);
  const [pageIndex, setPageIndex] = useState(0);
  const [nextCursor, setNextCursor] = useState<string | undefined>(undefined);
  const [pageLoading, setPageLoading] = useState(false);
  // Pages requested for the current filters, keyed by cursor. Previous is instant, and the next
  // page is requested when the pointer reaches Next, so Next usually is too. A new Map when the
  // filters change; an entry older than PAGE_CACHE_MS is shown, then requested again (see showPage).
  // settled: the response has arrived (a page requested on hover may still be on its way)
  const pageCache = useRef(
    new Map<string, { request: Promise<CallLogsResponse>; at: number; settled: boolean }>(),
  );
  // How many calls the filters match, requested after the first page shows; null until it arrives
  const [totalCount, setTotalCount] = useState<number | null>(null);
  // Bumped when the filters change, so a count for older filters is dropped
  const filtersSeq = useRef(0);
  const [copiedRoomName, setCopiedRoomName] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState<{
    url: string;
    callDate?: string;
    phoneNumber?: string;
    isVideo?: boolean;
    callId?: string;
    videos?: any;
  } | null>(null);
  // Unified view state
  const [selectedCallDetails, setSelectedCallDetails] = useState<{
    id: string;
    roomName: string | null;
    externalCallId?: string | null;
    organizationId?: string | null;
    // the agent that handled the call, linked from the header
    agent?: { id: string; name: string } | null;
    // Langfuse trace id: the call's external_call_id without dashes (see traceIdFor in voxi-2-0)
    traceId?: string | null;
    transcript: any[] | null;
    summary?: string | null;
    grammarAnalysis?: any | null;
    callDate: string;
    callInfo: {
      phoneNumber?: string;
      direction?: string;
      duration?: number;
    };
    recording?: {
      url: string;
      isVideo?: boolean;
      videos?: any;
    };
    cost?: CallCost;
    // "provider · model" per stage, from call_logs.{stt,llm,tts}_config
    models?: {
      stt: string | null;
      llm: string | null;
      tts: string | null;
    };
    language?: CallLanguageSummary | null;
  } | null>(null);
  // Call Diagnose: internal tool, shown only to allowed users (see useCanDiagnose)
  const { canDiagnose } = useCanDiagnose();
  const [diagnoseOpen, setDiagnoseOpen] = useState(false);
  const [diagnosisCounts, setDiagnosisCounts] = useState<Record<string, number>>({});
  const refreshDiagnosisCount = (callId: string) => {
    callDiagnosisService
      .getDiagnosisCounts([callId])
      .then((counts) => setDiagnosisCounts((prev) => ({ ...prev, ...counts })))
      .catch(() => {});
  };
  // One lookup per visible page, for the row badges
  useEffect(() => {
    if (!canDiagnose || callLogs.length === 0) return;
    let active = true;
    callDiagnosisService
      .getDiagnosisCounts(callLogs.map((l) => l.id))
      .then((counts) => active && setDiagnosisCounts((prev) => ({ ...prev, ...counts })))
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [canDiagnose, callLogs]);
  const [loadingVideos, setLoadingVideos] = useState(false);
  const [loadingDetailsId, setLoadingDetailsId] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  // Playback position of the call recording, used to sync the transcript bubbles
  const [playbackTime, setPlaybackTime] = useState(0);
  const detailsAudioRef = useRef<AudioPlayerHandle>(null);

  // Bumped by each showPage call; a response from an older call is dropped
  const fetchSeq = useRef(0);

  useEffect(() => {
    if (embedded) return;
    const { search, startDate, endDate, status, endedReason } = filtersFromParams(searchParams);
    setFilters((prev) => {
      const next = { ...prev, search, startDate, endDate, status, endedReason };
      if (
        next.search === prev.search &&
        next.startDate === prev.startDate &&
        next.endDate === prev.endDate &&
        next.status === prev.status &&
        next.endedReason === prev.endedReason
      ) {
        return prev;
      }
      return next;
    });
    setSearchInput(search);
    setDateStartInput(startDate);
    setDateEndInput(endDate);
  }, [searchParams]);

  // How the numbers of the calls on this page are on their organization's do-not-call list, for the badge
  const [doNotCallListings, setDoNotCallListings] = useState<Map<string, DoNotCallListing>>(new Map());
  useEffect(() => {
    if (callLogs.length === 0) return;
    doNotCallService
      .getListings(callLogs.map((log) => ({ organizationId: log.organization_id, phoneNumber: getPhoneNumber(log) })))
      .then(setDoNotCallListings);
  }, [callLogs]);

  useEffect(() => {
    // Wait for the organization; NavGuard normally has it before the page renders
    if (!organizationId) return;
    // New filters or organization: back to the first page, and the cached pages belong to the old ones
    pageCache.current = new Map();
    setPageCursors([undefined]);
    setTotalCount(null);
    const run = ++filtersSeq.current;
    // The count comes after the first page has shown, so the rows never wait for it
    showPage(0, undefined, true).then(() => {
      if (run !== filtersSeq.current) return;
      callLogService
        .getEstimatedTotal(buildApiFilters())
        .then((count) => {
          if (run === filtersSeq.current) setTotalCount(count);
        })
        .catch(() => {});
    });
    if (embedded) return;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (filters.search) next.set("search", filters.search);
      else next.delete("search");
      if (filters.startDate) next.set("startDate", filters.startDate);
      else next.delete("startDate");
      if (filters.endDate) next.set("endDate", filters.endDate);
      else next.delete("endDate");
      if (filters.status) next.set("status", filters.status);
      else next.delete("status");
      if (filters.endedReason) next.set("endedReason", filters.endedReason);
      else next.delete("endedReason");
      return next;
    }, { replace: true });
  }, [filters, organizationId]);

  // Debounce the search input. A term under MIN_SEARCH_LENGTH searches nothing (the list stays as
  // if the box were empty), so typing "98" on the way to a number does not fire a 2-character search.
  useEffect(() => {
    const timer = setTimeout(() => {
      const term = searchTerm(searchInput);
      if (term !== filters.search) {
        setFilters(prev => ({ ...prev, search: term }));
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput, filters.search]);

  // Auto-fullscreen for video calls
  useEffect(() => {
    const autoFullscreen = async () => {
      if (selectedMedia?.isVideo && dialogRef.current && !loadingVideos) {
        try {
          await dialogRef.current.requestFullscreen();
        } catch (error) {
          console.log("Could not enter fullscreen:", error);
        }
      }
    };

    // Small delay to ensure the dialog is fully rendered
    if (selectedMedia?.isVideo && !loadingVideos) {
      const timer = setTimeout(autoFullscreen, 300);
      return () => clearTimeout(timer);
    }
  }, [selectedMedia, loadingVideos]);

  const PAGE_SIZE = 20;

  const buildApiFilters = (cursor?: string): CallLogFilters => ({
    limit: PAGE_SIZE,
    method: "cursor",
    // The total is requested on its own after the first page (see the filters effect)
    estimate: "skip",
    ...(cursor && { cursor }),
    ...(organizationId && { organizationId }),
    ...(assistantId && { assistantId }),
    // searchTerm again here: a short term can also arrive from the URL (?search=98)
    ...(searchTerm(filters.search) && { search: searchTerm(filters.search) }),
    ...(filters.startDate && { startDate: filters.startDate }),
    ...(filters.endDate && { endDate: filters.endDate }),
    ...(filters.status && {
      status: (() => {
        const statusConfig =
          CALL_LOG_STATUS[filters.status as keyof typeof CALL_LOG_STATUS];
        if (Array.isArray(statusConfig)) {
          return statusConfig[0];
        }
        return statusConfig;
      })(),
    }),
    ...(filters.endedReason && {
      endedReason:
        CALL_END_REASONS[
          filters.endedReason as keyof typeof CALL_END_REASONS
        ],
    }),
    // Transcript and summary are left out; they load when a call is opened
  });

  // A kept page younger than this is shown as is. An older one is still shown at once, then
  // requested again behind it (see showPage): there is no Refresh button, so going back must not
  // leave a page as it was when first opened (new calls missing, a finished call still in progress,
  // and after 3 h dead recording links: get_call_logs presigns them for 3 h).
  const PAGE_CACHE_MS = 60_000;

  const isFresh = (kept: { at: number }) => Date.now() - kept.at < PAGE_CACHE_MS;

  // One page's request, shared: showing a page that is already requested (or prefetched) reuses it.
  // force asks the server again even when a fresh copy is kept.
  const fetchPage = (cursor?: string, force = false): Promise<CallLogsResponse> => {
    const cache = pageCache.current;
    const key = cursor ?? "";
    const kept = cache.get(key);
    if (kept && !force && isFresh(kept)) return kept.request;

    const request = callLogService.getCallLogs(buildApiFilters(cursor));
    const entry = { request, at: Date.now(), settled: false };
    cache.set(key, entry);
    request.then(() => {
      entry.settled = true;
    }, () => {});
    // A failed page is not kept, so trying again asks the server again. Only this entry: a newer
    // request for the same page may have replaced it meanwhile.
    request.catch(() => {
      if (cache.get(key) === entry) cache.delete(key);
    });
    return request;
  };

  // showLoading: replace the table with the spinner (first page of new filters). Otherwise a kept
  // page shows at once, and a page not yet requested keeps the current rows, dimmed, until it
  // arrives.
  const showPage = async (index: number, cursor: string | undefined, showLoading = false) => {
    // Typing a search fires one request per pause, and Next can be pressed quickly; only the latest
    // may update the list, or a slow earlier one could land last and show the wrong rows
    const seq = ++fetchSeq.current;
    const isStale = () => seq !== fetchSeq.current;
    const kept = pageCache.current.get(cursor ?? "");
    if (showLoading) setLoading(true);
    // Dimmed (and Previous/Next disabled) unless the page is already here. A page requested on
    // hover counts as not here until its response arrives, or a quick click would seem ignored.
    else if (!kept?.settled) setPageLoading(true);
    setError(null);

    const apply = (response: CallLogsResponse) => {
      setCallLogs(response.data || []);
      setPageIndex(index);
      setNextCursor(response.pagination?.hasMore ? response.pagination.nextCursor : undefined);
    };

    try {
      const response = await (kept ? kept.request : fetchPage(cursor));
      if (isStale()) return;
      // The server says hasMore whenever a page is full, so when exactly a page's worth was left
      // the next page is empty: stay on this one, without a Next
      if (index > 0 && (response.data?.length ?? 0) === 0) {
        setNextCursor(undefined);
        return;
      }
      apply(response);
      // Shown from memory but more than a minute old: ask again behind the rows, which update in
      // place. A failure here leaves the rows shown; it is not worth an error.
      if (kept && !isFresh(kept)) {
        fetchPage(cursor, true)
          .then((fresh) => {
            if (!isStale()) apply(fresh);
          })
          .catch(() => {});
      }
    } catch (error) {
      if (isStale()) return;
      console.error("Error fetching call logs:", error);
      setError(error instanceof Error ? error.message : "Failed to fetch call logs");
    } finally {
      if (!isStale()) {
        setLoading(false);
        setPageLoading(false);
      }
    }
  };

  const goToNextPage = () => {
    if (!nextCursor) return;
    const index = pageIndex + 1;
    setPageCursors((prev) => [...prev.slice(0, index), nextCursor]);
    showPage(index, nextCursor);
  };

  // Pointer on Next (or focus on it): request the next page now. The click comes a few hundred ms
  // later, usually after the page has arrived, and a visit that never pages requests nothing extra.
  const prefetchNextPage = () => {
    if (nextCursor) fetchPage(nextCursor).catch(() => {});
  };

  // "1–20 of 65": the rows on this page, then the total once it has arrived. The total is counted
  // when the filters change, so calls arriving later can put the rows past it; it is never shown
  // below the rows already reached.
  const pageRangeLabel = () => {
    const first = pageIndex * PAGE_SIZE + 1;
    const last = first + callLogs.length - 1;
    const range = `${first.toLocaleString()}–${last.toLocaleString()}`;
    if (totalCount === null) return range;
    return `${range} of ${Math.max(totalCount, last).toLocaleString()}`;
  };

  const goToPreviousPage = () => {
    if (pageIndex === 0) return;
    showPage(pageIndex - 1, pageCursors[pageIndex - 1]);
  };

  /**
   * Room names look like "interview-<uuid>" or "outbound-call-v2-<uuid>".
   * Split the readable prefix from the trailing id so the column can show the
   * call type on one line and the (truncated) id beneath it.
   */
  const splitRoomName = (room?: string | null) => {
    if (!room) return { label: "-", id: "" };
    const match = room.match(
      /^(.*?)-((?:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}).*)$/i,
    );
    if (match) return { label: match[1], id: match[2] };
    return { label: room, id: "" };
  };

  const formatDuration = (seconds: number) => {
    if (!seconds) return "0:00";
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  const getPhoneNumber = (log: CallLog) => {
    // Check if this is a web call (has room_name but no phone numbers)
    if (log.room_name && (!log.caller_number && !log.called_number && !log.to_number && !log.from_number)) {
      return "Web Call";
    }
    
    // For regular phone calls - use to_number for outbound, from_number for inbound
    let phoneNumber;
    if (log.call_type === "inbound") {
      phoneNumber = log.from_number || log.caller_number;
    } else {
      phoneNumber = log.to_number || log.called_number;
    }
    
    // If no phone number is available, check if it's a web call
    if (!phoneNumber && log.room_name) {
      return "Web Call";
    }
    
    return phoneNumber || "Unknown";
  };


  const organizeVideoData = (compositeUrl: string, additionalVideos: any[]) => {
    const videoOptions = [];
    const participantVideos: Record<string, any> = {};

    // Add main recording video first (if it exists)
    if (compositeUrl) {
      videoOptions.push({
        type: "composite",
        label: "Main Recording",
        url: compositeUrl,
        participant: null,
      });
    }

    // Process additional videos
    additionalVideos.forEach((video) => {
      const { participant_identity, video_type, video_url } = video;

      if (!participantVideos[participant_identity]) {
        participantVideos[participant_identity] = {
          participant_identity,
          webcam: null,
          screenshare: null,
        };
      }

      const videoData = {
        url: video_url,
        egress_id: video.egress_id,
        file_size: video.file_size,
        duration_seconds: video.duration_seconds,
        created_at: video.created_at,
      };

      if (video_type === "webcam") {
        participantVideos[participant_identity].webcam = videoData;
        videoOptions.push({
          type: "webcam",
          label: `${participant_identity} - Webcam`,
          url: video_url,
          participant: participant_identity,
        });
      } else if (video_type === "screenshare" || video_type === "screen") {
        participantVideos[participant_identity].screenshare = videoData;
        videoOptions.push({
          type: "screenshare",
          label: `${participant_identity} - Screen Share`,
          url: video_url,
          participant: participant_identity,
        });
      }
    });

    return {
      composite_video: {
        url: compositeUrl,
        type: "composite",
      },
      participant_videos: participantVideos,
      video_options: videoOptions,
    };
  };

  const openMediaPlayer = async (url: string, log: CallLog) => {
    if (!url) return;

    const phoneNumber = getPhoneNumber(log);

    // Improved video detection - check for video file extensions and room_name indicating web call
    const isVideo = 
      url.toLowerCase().endsWith(".mp4") || 
      url.toLowerCase().endsWith(".webm") || 
      url.toLowerCase().endsWith(".mov") || 
      url.toLowerCase().endsWith(".avi") ||
      url.toLowerCase().endsWith(".mkv") ||
      url.toLowerCase().endsWith(".m4v") ||
      (log.room_name && url && !url.toLowerCase().endsWith(".mp3") && !url.toLowerCase().endsWith(".wav") && !url.toLowerCase().endsWith(".m4a")); // Web calls with room_name are likely video calls unless they're clearly audio

    if (isVideo) {
      // For video calls, fetch additional videos from Supabase
      setLoadingVideos(true);
      try {
        // Fetch additional videos from the call_log_additional_videos table
        const { data: additionalVideos, error } = await supabase
          .from("call_log_additional_videos")
          .select("*")
          .eq("call_log_id", log.id);

        if (error) {
          console.error("Error fetching additional videos:", error);
          // Continue with just the main video even if additional videos fail
        }

        // Organize the video data in the expected format
        const videos = organizeVideoData(url, additionalVideos || []);

        setSelectedMedia({
          url,
          callDate: log.created_at,
          phoneNumber,
          isVideo,
          callId: log.id,
          videos,
        });
      } catch (error) {
        console.error("Error fetching video data:", error);
        // Fallback: show just the main video without additional videos
        setSelectedMedia({
          url,
          callDate: log.created_at,
          phoneNumber,
          isVideo,
          callId: log.id,
        });
      } finally {
        setLoadingVideos(false);
      }
      return;
    }

    // Default behavior for audio or if video fetch fails
    setSelectedMedia({
      url,
      callDate: log.created_at,
      phoneNumber,
      isVideo,
      callId: log.id,
    });
  };

  // Unified view for both recording and transcript
  const viewCallDetails = async (log: CallLog) => {
    try {
      setLoadingDetailsId(log.id);

      // Fetch full call log details with transcript and summary
      const fullLog = await callLogService.getCallLogById(log.id);
      if (!fullLog) {
        console.error("Failed to load call log details");
        setLoadingDetailsId(null);
        return;
      }

      console.log("[viewCallDetails] Full log data:", {
        id: fullLog.id,
        has_transcript: !!fullLog.transcript,
        has_summary: !!fullLog.summary,
        has_cost: !!fullLog.cost,
        has_grammar_analysis: !!fullLog.grammar_analysis,
        has_recording_url: !!fullLog.recording_url,
        room_name: fullLog.room_name
      });

      const phoneNumber = getPhoneNumber(fullLog);

      // Parse transcript
      let parsedTranscript = null;
      if (fullLog.transcript) {
        try {
          // Transcript is stored as a string in the database, parse it
          parsedTranscript = typeof fullLog.transcript === 'string'
            ? JSON.parse(fullLog.transcript)
            : fullLog.transcript;

          if (!Array.isArray(parsedTranscript)) {
            if (parsedTranscript && typeof parsedTranscript === 'object') {
              parsedTranscript = [parsedTranscript];
            } else {
              parsedTranscript = null;
            }
          }
          console.log("[viewCallDetails] Transcript parsed successfully:", parsedTranscript?.length || 0, "items");
        } catch (error) {
          console.error("[viewCallDetails] Error parsing transcript:", error);
          console.error("[viewCallDetails] Raw transcript data:", fullLog.transcript?.substring(0, 200));
          parsedTranscript = null;
        }
      } else {
        console.log("[viewCallDetails] No transcript data available");
      }

      // Prepare recording info if available
      let recordingInfo = undefined;
      if (fullLog.recording_url) {
        // Strip query params (e.g. presigned URL ?X-Amz-...) before checking extension
        const urlPath = fullLog.recording_url.split("?")[0].toLowerCase();

        // First check if it's explicitly an audio file
        const isAudio = urlPath.endsWith(".mp3") || urlPath.endsWith(".wav") || urlPath.endsWith(".m4a") || urlPath.endsWith(".ogg");

        // Only consider it video if it has video extensions
        const isVideo = !isAudio && (
          urlPath.endsWith(".mp4") ||
          urlPath.endsWith(".webm") ||
          urlPath.endsWith(".mov") ||
          urlPath.endsWith(".avi") ||
          urlPath.endsWith(".mkv") ||
          urlPath.endsWith(".m4v")
        );

        // Fetch additional videos for video calls
        let videos = undefined;
        if (isVideo) {
          setLoadingVideos(true);
          try {
            const { data: additionalVideos, error } = await supabase
              .from("call_log_additional_videos")
              .select("*")
              .eq("call_log_id", fullLog.id);

            if (!error && additionalVideos) {
              videos = organizeVideoData(fullLog.recording_url, additionalVideos);
            }
          } catch (error) {
            console.error("Error fetching additional videos:", error);
          } finally {
            setLoadingVideos(false);
          }
        }

        recordingInfo = {
          url: fullLog.recording_url,
          isVideo,
          videos
        };
      }

      // Parse cost data
      let costData: CallCost | undefined = undefined;
      if (fullLog.cost) {
        // Cost is stored as JSON/JSONB, might come as string or object
        costData = parseCallCost(fullLog.cost);
        console.log("[viewCallDetails] Cost data parsed:", costData);
      } else {
        console.log("[viewCallDetails] No cost data available");
      }

      // Log grammar analysis
      if (fullLog.grammar_analysis) {
        console.log("[viewCallDetails] Grammar analysis available:", {
          overall_score: fullLog.grammar_analysis.overall_score,
          confidence: fullLog.grammar_analysis.confidence,
          has_detailed_feedback: !!fullLog.grammar_analysis.detailed_feedback
        });
      } else {
        console.log("[viewCallDetails] No grammar analysis available");
      }

      // Set unified state
      const agentId = fullLog.assistant_id || log.assistant_id;
      setSelectedCallDetails({
        id: fullLog.id,
        externalCallId: fullLog.external_call_id ? String(fullLog.external_call_id) : null,
        organizationId: fullLog.organization_id || log.organization_id || null,
        roomName: fullLog.room_name || null,
        traceId: fullLog.external_call_id ? String(fullLog.external_call_id).replace(/-/g, "").toLowerCase() : null,
        agent: agentId
          ? {
              id: agentId,
              name: fullLog.assistant?.name || log.assistant?.name || log.assistant_name || "Agent",
            }
          : null,
        transcript: parsedTranscript,
        summary: fullLog.summary,
        grammarAnalysis: fullLog.grammar_analysis,
        callDate: fullLog.created_at,
        callInfo: {
          phoneNumber,
          direction: fullLog.call_type,
          duration: fullLog.call_duration || undefined,
        },
        recording: recordingInfo,
        cost: costData,
        models: {
          stt: formatModelLabel(fullLog.stt_config),
          llm: formatModelLabel(fullLog.llm_config),
          tts: formatModelLabel(fullLog.tts_config),
        },
        // call_logs.language is not in the generated CallLog type yet
        language: readCallLanguage((fullLog as any).language),
      });

      console.log("[viewCallDetails] Call details loaded successfully");

    } catch (error) {
      console.error("Error fetching call details:", error);
      setError("Failed to load call details");
    } finally {
      setLoadingDetailsId(null);
    }
  };

  const clearFilters = () => {
    setFilters({
      search: "",
      startDate: "",
      endDate: "",
      status: "",
      endedReason: "",
    });
    setSearchInput("");
    setDateStartInput("");
    setDateEndInput("");
    if (embedded) return;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("search");
      next.delete("startDate");
      next.delete("endDate");
      next.delete("status");
      next.delete("endedReason");
      return next;
    });
  };

  return (
    // On its own page DashboardLayout's <main> already pads the top
    <div className={`w-full bg-background text-foreground ${embedded ? "py-6" : "pb-6"}`}>
      <div className="flex flex-col gap-3 mb-6">
        {/* Filter Panel: search takes whatever width the other controls leave */}
        <div className="flex flex-wrap gap-3 items-end">
          {/* Search: number, room, or assistant */}
          <div className="flex flex-col gap-1 flex-1 min-w-[200px]">
            <label className="text-sm font-medium text-muted-foreground">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search by number, room name, or assistant..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="pl-10 pr-8"
              />
              {searchInput && (
                <button
                  onClick={() => setSearchInput("")}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            {searchInput.trim() && !searchTerm(searchInput) && (
              <p className="text-xs text-muted-foreground">
                Type at least {MIN_SEARCH_LENGTH} characters to search
              </p>
            )}
          </div>

          {/* Date Range */}
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-muted-foreground">Start Date</label>
            <Input
              type="date"
              value={dateStartInput}
              onChange={(e) => {
                const value = e.target.value;
                setDateStartInput(value);
                setFilters((prev) => ({ ...prev, startDate: value }));
              }}
              className="w-[170px]"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-muted-foreground">End Date</label>
            <Input
              type="date"
              value={dateEndInput}
              onChange={(e) => {
                const value = e.target.value;
                setDateEndInput(value);
                setFilters((prev) => ({ ...prev, endDate: value }));
              }}
              className="w-[170px]"
            />
          </div>

          <Button
            variant="outline"
            onClick={() => {
              setModalStatusFilter(filters.status || "all");
              setModalEndReasonFilter(filters.endedReason || "all");
              setIsFilterOpen(true);
            }}
            className="h-9"
            title="Filter by status and ended reason"
          >
            <Filter className="mr-1 h-4 w-4" />
            Filter
          </Button>

          {(searchInput || dateStartInput || dateEndInput || filters.status || filters.endedReason) && (
            <Button
              variant="outline"
              onClick={clearFilters}
              className="h-10"
            >
              <X className="mr-1 h-4 w-4" />
              Clear Search
            </Button>
          )}
        </div>
      </div>

      <Dialog open={isFilterOpen} onOpenChange={setIsFilterOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Filter Call Logs</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <p className="text-sm font-medium">Status</p>
              <Select
                value={modalStatusFilter}
                onValueChange={setModalStatusFilter}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent
                  position="popper"
                  side="bottom"
                  sideOffset={4}
                  avoidCollisions={false}
                  className="max-h-60 overflow-y-auto duration-0 data-[state=open]:translate-y-0"
                >
                  <SelectItem value="all">All statuses</SelectItem>
                  {Object.keys(CALL_LOG_STATUS).map((label) => (
                    <SelectItem key={label} value={label}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <p className="text-sm font-medium">Ended reason</p>
              <Select
                value={modalEndReasonFilter}
                onValueChange={setModalEndReasonFilter}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="All ended reasons" />
                </SelectTrigger>
                <SelectContent
                  position="popper"
                  side="bottom"
                  sideOffset={4}
                  avoidCollisions={false}
                  className="max-h-60 overflow-y-auto duration-0 data-[state=open]:translate-y-0"
                >
                  <SelectItem value="all">All ended reasons</SelectItem>
                  {Object.keys(CALL_END_REASONS).map((label) => (
                    <SelectItem key={label} value={label}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button
              variant="ghost"
              onClick={() => {
                setModalStatusFilter("all");
                setModalEndReasonFilter("all");
              }}
            >
              Clear
            </Button>
            <Button
              onClick={() => {
                const newStatus =
                  modalStatusFilter === "all" ? "" : modalStatusFilter;
                const newEndReason =
                  modalEndReasonFilter === "all" ? "" : modalEndReasonFilter;
                setFilters((prev) => ({
                  ...prev,
                  status: newStatus,
                  endedReason: newEndReason,
                }));
                setIsFilterOpen(false);
              }}
            >
              Apply
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {error && (
        <div className="mb-4 p-4 border border-danger-line rounded-md bg-danger-bg text-danger">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : callLogs.length === 0 ? (
        <NoData
          icon={<Phone className="h-12 w-12 text-muted-foreground/50" />}
          title="No call logs found"
          description={
            filters.search || filters.status || filters.endedReason
              ? "No calls match your search or filters. Try adjusting them."
              : "When calls are made through the system, they will appear here."
          }
        />
      ) : (
        <div className={`border rounded-md transition-opacity ${pageLoading ? "opacity-60" : ""}`}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Room</TableHead>
                <TableHead>Phone Number</TableHead>
                <TableHead>Direction / Assistant</TableHead>
                <TableHead>Duration</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Outcome</TableHead>
                <TableHead>Ended Reason</TableHead>
                <TableHead className="text-right">Details</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {callLogs.map((log) => (
                <TableRow
                  key={log.id}
                  className="cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  tabIndex={0}
                  onClick={() => {
                    if (loadingDetailsId !== log.id) viewCallDetails(log);
                  }}
                  onKeyDown={(e) => {
                    // Only the row itself: Enter or Space on a button inside it keeps doing that button's job.
                    if (e.target !== e.currentTarget || (e.key !== "Enter" && e.key !== " ")) return;
                    e.preventDefault();
                    if (loadingDetailsId !== log.id) viewCallDetails(log);
                  }}
                >
                  <TableCell className="max-w-[280px]">
                    {(() => {
                      const { label, id } = splitRoomName(log.room_name);
                      return (
                        <div className="flex flex-col gap-0.5">
                          <span className="text-sm font-medium text-foreground">
                            {label}
                          </span>
                          {id && (
                            <span
                              className="block max-w-[260px] truncate font-mono text-[12px] text-muted-foreground"
                              title={log.room_name || ""}
                            >
                              {id}
                            </span>
                          )}
                          <span className="text-xs text-muted-foreground">
                            {format(new Date(log.created_at), "MMM d, yyyy h:mm a")}
                          </span>
                          {canDiagnose && diagnosisCounts[log.id] > 0 && (
                            <span
                              className="inline-flex items-center gap-1 text-xs text-muted-foreground"
                              title={`${diagnosisCounts[log.id]} diagnoses`}
                            >
                              <Stethoscope className="h-3 w-3" />
                              {diagnosisCounts[log.id]}
                            </span>
                          )}
                        </div>
                      );
                    })()}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col items-start gap-1">
                      {getPhoneNumber(log)}
                      {(() => {
                        // The request itself, else the organization's listing, else this call's agent's.
                        if (log.dnc_requested === true) return <DoNotCallBadge requested />;
                        const listing = doNotCallListings.get(doNotCallKey(log.organization_id, getPhoneNumber(log)));
                        if (listing?.organization) return <DoNotCallBadge />;
                        if (log.assistant_id && listing?.assistantIds.has(log.assistant_id)) return <DoNotCallBadge thisAgent />;
                        return null;
                      })()}
                    </div>
                  </TableCell>
                  <TableCell className="max-w-[180px]">
                    <div className="flex flex-col items-start gap-1">
                      <span className="inline-flex rounded-full bg-secondary px-2 py-0.5 text-xs font-medium text-foreground">
                        {log.call_type}
                      </span>
                      <span
                        className="block max-w-[170px] truncate text-xs text-muted-foreground"
                        title={log.assistant?.name || log.assistant_name || "N/A"}
                      >
                        {log.assistant?.name || log.assistant_name || "N/A"}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    {formatDuration(log.call_duration || 0)}
                  </TableCell>
                  <TableCell>
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                        log.status === "completed"
                          ? "bg-good-bg text-good dark:bg-good-bg/30 dark:text-good"
                          : log.status === "failed"
                          ? "bg-danger-bg text-danger dark:bg-danger-bg/30 dark:text-danger"
                          : "bg-mid-bg text-mid dark:bg-mid-bg/30 dark:text-mid"
                      }`}
                    >
                      {log.status}
                    </span>
                  </TableCell>
                  <TableCell className="min-w-[140px] max-w-[200px]">
                    {/* post-call analysis verdict; empty until analysis has run */}
                    <div className="break-words whitespace-normal">
                      {log.outcome_reason || "-"}
                    </div>
                  </TableCell>
                  <TableCell className="min-w-[200px] max-w-[280px]">
                    {/* wraps instead of truncating so the full reason is readable */}
                    <div className="break-words whitespace-normal">
                      {log.ended_reason || "-"}
                    </div>
                  </TableCell>
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="flex justify-end space-x-2">
                      {/* Unified view button - shows recording, transcript, and cost */}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => viewCallDetails(log)}
                        disabled={loadingDetailsId === log.id}
                        title="View call details (recording, transcript & cost)"
                        className="text-foreground hover:text-foreground/80"
                      >
                        {loadingDetailsId === log.id ? (
                          <RefreshCw className="h-4 w-4 animate-spin" />
                        ) : (
                          <>
                            <FileText className="h-4 w-4" />
                          </>
                        )}
                      </Button>

                      {/* Latency Dashboard button */}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/call/${log.id}/latency`)}
                        title="View call latency metrics"
                        className="text-foreground hover:text-foreground/80"
                      >
                        <Activity className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {!loading && callLogs.length > 0 && (
        <div className="flex items-center justify-between mt-4">
          <span className="text-sm text-muted-foreground">{pageRangeLabel()}</span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={goToPreviousPage}
              disabled={pageIndex === 0 || pageLoading}
            >
              <ChevronLeft className="mr-1 h-4 w-4" />
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={goToNextPage}
              onMouseEnter={prefetchNextPage}
              onFocus={prefetchNextPage}
              disabled={!nextCursor || pageLoading}
            >
              Next
              <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Unified Call Details Dialog - Shows both recording and transcript */}
      {selectedCallDetails && (
        <Dialog
          open={!!selectedCallDetails}
          onOpenChange={(open) => {
            if (!open) {
              if (document.fullscreenElement) {
                document.exitFullscreen().catch(console.error);
              }
              setDiagnoseOpen(false);
              setSelectedCallDetails(null);
              setPlaybackTime(0);
            }
          }}
        >
          <DialogContent
            ref={dialogRef}
            // The Diagnose sheet is nested in this dialog; its clicks and Escape must not close the dialog
            onInteractOutside={(e) => diagnoseOpen && e.preventDefault()}
            onEscapeKeyDown={(e) => diagnoseOpen && e.preventDefault()}
            className="w-[95vw] sm:max-w-[1500px] max-h-[95vh] h-[95vh] flex flex-col p-0"
          >
            <DialogHeader className="p-6 pb-4 border-b">
              <DialogTitle>Call Details</DialogTitle>
              {selectedCallDetails.callDate && (
                <DialogDescription className="space-y-1">
                  <div>
                    {format(new Date(selectedCallDetails.callDate), "MMMM d, yyyy h:mm a")}
                    {selectedCallDetails.callInfo && (
                      <span className="ml-2">
                        {selectedCallDetails.callInfo.phoneNumber && (
                          <span className="mr-2">• {selectedCallDetails.callInfo.phoneNumber}</span>
                        )}
                        {selectedCallDetails.callInfo.direction && (
                          <span className="mr-2">• {selectedCallDetails.callInfo.direction}</span>
                        )}
                        {selectedCallDetails.callInfo.duration !== undefined && (
                          <span>• {formatDuration(selectedCallDetails.callInfo.duration)} duration</span>
                        )}
                      </span>
                    )}
                  </div>
                  {selectedCallDetails.agent && (
                    <div className="text-xs">
                      <span>Agent: </span>
                      {/* Link, not <a href>: it adds the router's basename */}
                      <Link
                        to={`/assistants/${selectedCallDetails.agent.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 font-medium text-primary hover:underline"
                        title="Open this agent in a new tab"
                      >
                        {selectedCallDetails.agent.name}
                        <ExternalLink className="h-3 w-3" />
                      </Link>
                      {langfuseTraceUrl(selectedCallDetails.traceId) && (
                        <a
                          href={langfuseTraceUrl(selectedCallDetails.traceId)!}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="ml-2 inline-flex items-center align-middle text-muted-foreground hover:text-primary"
                          title="Open this call's traces in Langfuse"
                          aria-label="Open traces in Langfuse"
                        >
                          <Activity className="h-3.5 w-3.5" />
                        </a>
                      )}
                    </div>
                  )}
                  {canDiagnose && (
                    <div className="text-xs">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-6 gap-1 px-2 text-xs"
                        title="Diagnose this call"
                        aria-label="Diagnose this call"
                        onClick={() => setDiagnoseOpen(true)}
                      >
                        <Stethoscope className="h-3.5 w-3.5" />
                        Diagnose
                      </Button>
                    </div>
                  )}
                  {selectedCallDetails.roomName && (
                    <div className="flex items-center gap-2 text-xs font-mono text-muted-foreground">
                      <span>Room: {selectedCallDetails.roomName}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-5 w-5 p-0"
                        onClick={() => {
                          navigator.clipboard.writeText(selectedCallDetails.roomName!);
                          setCopiedRoomName(true);
                          setTimeout(() => setCopiedRoomName(false), 2000);
                        }}
                        title="Copy room name"
                      >
                        {copiedRoomName ? (
                          <Check className="h-3 w-3 text-good" />
                        ) : (
                          <Copy className="h-3 w-3" />
                        )}
                      </Button>
                    </div>
                  )}
                  {selectedCallDetails.language && (
                    <div className="text-xs text-muted-foreground">
                      <div>
                        Language:{" "}
                        {selectedCallDetails.language.start === selectedCallDetails.language.final
                          ? languageName(selectedCallDetails.language.start)
                          : `${languageName(selectedCallDetails.language.start)} → ${languageName(selectedCallDetails.language.final)}`}
                      </div>
                      {selectedCallDetails.language.switches.map((item, index) => (
                        <div key={index} className="pl-3">
                          Turn {item.turn}: {languageName(item.from)} to {languageName(item.to)}{" "}
                          ({item.reason === "ask" ? "caller asked" : "followed the caller"})
                        </div>
                      ))}
                    </div>
                  )}
                </DialogDescription>
              )}
            </DialogHeader>

            <div className="flex-1 flex flex-col overflow-hidden">
              <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] gap-6 h-full p-6 overflow-hidden">
                {/* Left side: Recording, Cost & Summary */}
                <div className="flex flex-col min-h-0 overflow-hidden">
                  <h3 className="font-semibold text-lg mb-4 flex-shrink-0">Recording & Info</h3>
                  <div className="flex-1 min-h-0 overflow-hidden">
                    <ScrollArea className="h-full">
                      <div className="space-y-4 pr-4">
                        {/* Recording/Video Player */}
                        {loadingVideos ? (
                          <div className="flex justify-center items-center h-64 border rounded-lg">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                          </div>
                        ) : selectedCallDetails.recording ? (
                          <div className="flex flex-col space-y-2">
                            {selectedCallDetails.recording.isVideo ? (
                              <>
                                {selectedCallDetails.recording.videos?.video_options?.length > 1 ? (
                                  <MultiVideoPlayer videos={selectedCallDetails.recording.videos} />
                                ) : (
                                  <VideoPlayer
                                    url={selectedCallDetails.recording.url}
                                    className="w-full aspect-video"
                                  />
                                )}
                                <div className="flex justify-end">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={async () => {
                                      try {
                                        const url = selectedCallDetails.recording!.url;
                                        const date = selectedCallDetails.callDate
                                          ? format(new Date(selectedCallDetails.callDate), "yyyyMMdd_HHmmss")
                                          : "recording";
                                        const filename = `call_video_${date}.mp4`;
                                        const link = document.createElement("a");
                                        link.href = url;
                                        link.download = filename;
                                        document.body.appendChild(link);
                                        link.click();
                                        document.body.removeChild(link);
                                      } catch (error) {
                                        console.error("Error downloading video:", error);
                                      }
                                    }}
                                    title="Download Video"
                                  >
                                    <Download className="h-4 w-4 mr-2" />
                                    Download
                                  </Button>
                                </div>
                              </>
                            ) : (
                              <div className="space-y-2">
                                <AudioPlayer
                                  ref={detailsAudioRef}
                                  url={selectedCallDetails.recording.url}
                                  onTimeUpdate={setPlaybackTime}
                                />
                                <div className="flex justify-end">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={async () => {
                                      try {
                                        const url = selectedCallDetails.recording!.url;
                                        const date = selectedCallDetails.callDate
                                          ? format(new Date(selectedCallDetails.callDate), "yyyyMMdd_HHmmss")
                                          : "recording";
                                        const filename = `call_recording_${date}.mp3`;
                                        const link = document.createElement("a");
                                        link.href = url;
                                        link.download = filename;
                                        document.body.appendChild(link);
                                        link.click();
                                        document.body.removeChild(link);
                                      } catch (error) {
                                        console.error("Error downloading recording:", error);
                                      }
                                    }}
                                    title="Download Recording"
                                  >
                                    <Download className="h-4 w-4 mr-2" />
                                    Download
                                  </Button>
                                </div>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="flex items-center justify-center h-64 border rounded-lg bg-muted/20">
                            <p className="text-muted-foreground">No recording available</p>
                          </div>
                        )}

                        {/* Display cost breakdown if available */}
                        {isPlatformAdmin && selectedCallDetails.cost && (
                          <div className="border border-border rounded-lg p-4 bg-muted/50 dark:bg-muted/20">
                            <h4 className="font-medium mb-3 text-sm uppercase tracking-wide text-foreground">
                              Cost Breakdown
                            </h4>
                            <div className="space-y-2 text-sm">
                              {callCostRows(selectedCallDetails.cost, selectedCallDetails.models).map(({ label, cost, detail }) => (
                                <div key={label} className="flex justify-between items-start gap-4">
                                  <div className="min-w-0">
                                    <div className="text-muted-foreground">{label}:</div>
                                    {detail && (
                                      <div className="text-xs font-mono text-muted-foreground/80 break-all">{detail}</div>
                                    )}
                                  </div>
                                  <span className="font-medium">${cost.toFixed(4)}</span>
                                </div>
                              ))}
                              <div className="flex justify-between font-semibold border-t pt-2 mt-2">
                                <span>Total Cost:</span>
                                <span>${selectedCallDetails.cost.total_cost.toFixed(4)}</span>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Display summary if available */}
                        {selectedCallDetails.summary && (
                          <div className="border border-border rounded-lg p-4 bg-muted/50 dark:bg-muted/20">
                            <h4 className="font-medium mb-2 text-sm uppercase tracking-wide text-foreground">
                              Call Summary
                            </h4>
                            <div className="text-sm whitespace-pre-wrap text-foreground">
                              {selectedCallDetails.summary}
                            </div>
                          </div>
                        )}
                      </div>
                    </ScrollArea>
                  </div>
                </div>

                {/* Right side: Transcript & Grammar Analysis */}
                <div className="flex flex-col min-h-0 overflow-hidden">
                  <h3 className="font-semibold text-lg mb-4 flex-shrink-0">Transcript & Analysis</h3>
                  <div className="flex-1 min-h-0 overflow-hidden border rounded-lg">
                    <ScrollArea className="h-full">
                      <div className="space-y-4 p-4">

                      {/* Display grammar analysis if available */}
                      {selectedCallDetails.grammarAnalysis && (
                        <div className="border border-border rounded-lg p-4 bg-muted/50 dark:bg-muted/20">
                          <h4 className="font-medium mb-4 text-sm uppercase tracking-wide text-foreground">
                            Grammar Analysis
                          </h4>

                          {/* Overall Score */}
                          {selectedCallDetails.grammarAnalysis.overall_score !== undefined && (
                            <div className="mb-4 flex items-center space-x-4">
                              <div className="flex items-center space-x-2">
                                <span className="text-sm font-medium">Overall Score:</span>
                                <span
                                  className={`px-3 py-1 rounded-full text-sm font-medium ${
                                    selectedCallDetails.grammarAnalysis.overall_score >= 80
                                      ? "bg-good-bg text-good dark:bg-good-bg/30 dark:text-good"
                                      : selectedCallDetails.grammarAnalysis.overall_score >= 60
                                      ? "bg-mid-bg text-mid dark:bg-mid-bg/30 dark:text-mid"
                                      : "bg-danger-bg text-danger dark:bg-danger-bg/30 dark:text-danger"
                                  }`}
                                >
                                  {selectedCallDetails.grammarAnalysis.overall_score}/100
                                </span>
                              </div>
                              {selectedCallDetails.grammarAnalysis.confidence !== undefined && (
                                <div className="flex items-center space-x-2">
                                  <span className="text-sm text-muted-foreground">Confidence:</span>
                                  <span className="text-sm font-medium">
                                    {Math.round(selectedCallDetails.grammarAnalysis.confidence * 100)}%
                                  </span>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Error Categories */}
                          {selectedCallDetails.grammarAnalysis.error_categories &&
                           Object.entries(selectedCallDetails.grammarAnalysis.error_categories as Record<string, number>).some(([_, count]) => count > 0) && (
                            <div className="mb-4">
                              <h5 className="text-sm font-medium mb-2">Error Categories:</h5>
                              <div className="grid grid-cols-2 gap-2 text-sm">
                                {Object.entries(selectedCallDetails.grammarAnalysis.error_categories as Record<string, number>).map(([category, count]) => (
                                  count > 0 && (
                                    <div key={category} className="flex justify-between">
                                      <span className="text-muted-foreground">
                                        {category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:
                                      </span>
                                      <span className="font-medium">{count}</span>
                                    </div>
                                  )
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Analysis Error */}
                          {selectedCallDetails.grammarAnalysis.analysis_metadata?.error && (
                            <div className="p-3 bg-destructive/10 border border-destructive/20 rounded">
                              <p className="text-sm text-destructive">
                                ⚠️ Analysis Error: {selectedCallDetails.grammarAnalysis.analysis_metadata.error}
                              </p>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Transcript messages */}
                      {(() => {
                        if (!selectedCallDetails.transcript || selectedCallDetails.transcript.length === 0) {
                          return (
                            <div className="text-center text-muted-foreground py-8 border rounded-lg">
                              No transcript available for this call.
                            </div>
                          );
                        }

                        // Extract messages from the transcript
                        const allMessages: any[] = [];

                        try {
                          selectedCallDetails.transcript.forEach((transcriptItem) => {
                            // Handle nested structure
                            if (transcriptItem && transcriptItem.messages && Array.isArray(transcriptItem.messages)) {
                              transcriptItem.messages.forEach((msg: any) => {
                                if (msg && msg.role && msg.message) {
                                  if (msg.role === 'assistant' && isOnlyTrackingPhrases(msg.message)) {
                                    return;
                                  }
                                  allMessages.push({
                                    role: msg.role,
                                    message: msg.message,
                                    timestamp: msg.time,
                                    time: msg.time,
                                    endTime: msg.endTime,
                                    secondsFromStart: msg.secondsFromStart,
                                    interrupt: msg.interrupt
                                  });
                                }
                              });
                            }
                            // Handle direct message format
                            else if (transcriptItem && transcriptItem.role && transcriptItem.message) {
                              if (transcriptItem.role === 'assistant' && isOnlyTrackingPhrases(transcriptItem.message)) {
                                return;
                              }
                              allMessages.push({
                                role: transcriptItem.role,
                                message: transcriptItem.message,
                                timestamp: transcriptItem.timestamp || transcriptItem.time,
                                time: transcriptItem.time,
                                endTime: transcriptItem.endTime,
                                secondsFromStart: transcriptItem.secondsFromStart,
                                interrupt: transcriptItem.interrupt
                              });
                            }
                          });

                          // Only audio recordings support click-to-seek from a transcript bubble
                          const hasAudioRecording =
                            !!selectedCallDetails.recording && !selectedCallDetails.recording.isVideo;

                          return allMessages.length > 0 ? (
                            <div className="border rounded-lg p-4">
                              <h4 className="font-medium mb-3 text-sm uppercase tracking-wide">
                                Conversation
                                {hasAudioRecording && (
                                  <span className="ml-2 normal-case tracking-normal text-xs font-normal text-muted-foreground">
                                    (click a message to jump to that point in the recording)
                                  </span>
                                )}
                              </h4>
                              <ChatConversation
                                messages={allMessages}
                                currentTime={hasAudioRecording ? playbackTime : undefined}
                                onSeek={
                                  hasAudioRecording
                                    ? (seconds) => detailsAudioRef.current?.seekTo(seconds)
                                    : undefined
                                }
                              />
                            </div>
                          ) : (
                            <div className="text-center text-muted-foreground py-8 border rounded-lg">
                              <div>No conversation messages found in this call.</div>
                            </div>
                          );
                        } catch (error) {
                          console.error("Error processing transcript:", error);
                          return (
                            <div className="text-center text-muted-foreground py-8 border rounded-lg">
                              <div>Error processing transcript data.</div>
                              <div className="text-xs mt-2">Please check console for details.</div>
                            </div>
                          );
                        }
                      })()}
                      </div>
                    </ScrollArea>
                  </div>
                </div>
              </div>
            </div>
            {canDiagnose && (
              <DiagnoseSheet
                open={diagnoseOpen}
                onOpenChange={setDiagnoseOpen}
                onRunComplete={refreshDiagnosisCount}
                call={{
                  id: selectedCallDetails.id,
                  roomName: selectedCallDetails.roomName,
                  agentName: selectedCallDetails.agent?.name,
                  traceId: selectedCallDetails.traceId,
                  externalCallId: selectedCallDetails.externalCallId,
                  organizationId: selectedCallDetails.organizationId,
                  callDate: selectedCallDetails.callDate,
                  langfuseUrl: langfuseTraceUrl(selectedCallDetails.traceId),
                }}
              />
            )}
          </DialogContent>
        </Dialog>
      )}

      <Dialog
        open={!!selectedMedia}
        onOpenChange={(open) => {
          if (!open) {
            // Exit fullscreen if closing dialog
            if (document.fullscreenElement) {
              document.exitFullscreen().catch(console.error);
            }
            setSelectedMedia(null);
          }
        }}
      >
        <DialogContent
          ref={dialogRef}
          className={selectedMedia?.isVideo ? "w-[95vw] sm:max-w-[95vw] max-h-[95vh] h-[95vh]" : "w-[95vw] sm:max-w-xl"}
        >
          <DialogHeader>
            <DialogTitle>
              Call {selectedMedia?.isVideo ? "Video" : "Recording"}
            </DialogTitle>
            {selectedMedia?.callDate && (
              <DialogDescription>
                {format(
                  new Date(selectedMedia.callDate),
                  "MMMM d, yyyy h:mm a"
                )}
                {selectedMedia.phoneNumber && (
                  <span className="ml-2">• {selectedMedia.phoneNumber}</span>
                )}
              </DialogDescription>
            )}
          </DialogHeader>

          {loadingVideos ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : selectedMedia ? (
            selectedMedia.isVideo ? (
              <>
                {selectedMedia.videos?.video_options?.length > 1 ? (
                  <MultiVideoPlayer videos={selectedMedia.videos} />
                ) : (
                  <VideoPlayer
                    url={selectedMedia.url}
                    className="w-full aspect-video"
                  />
                )}
                <div className="flex justify-end mt-4">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={async () => {
                      try {
                        const url = selectedMedia.url;
                        const date = selectedMedia.callDate ? format(new Date(selectedMedia.callDate), "yyyyMMdd_HHmmss") : "recording";
                        const filename = `call_video_${date}.mp4`;
                        const link = document.createElement("a");
                        link.href = url;
                        link.download = filename;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                      } catch (error) {
                        console.error("Error downloading video:", error);
                      }
                    }}
                    title="Download Video"
                  >
                    <Download className="h-3 w-3" />
                  </Button>
                </div>
              </>
            ) : (
              <>
                <AudioPlayer url={selectedMedia.url} />
                <div className="flex justify-end mt-4">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={async () => {
                      try {
                        const url = selectedMedia.url;
                        const date = selectedMedia.callDate ? format(new Date(selectedMedia.callDate), "yyyyMMdd_HHmmss") : "recording";
                        const filename = `call_recording_${date}.mp3`;
                        const link = document.createElement("a");
                        link.href = url;
                        link.download = filename;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                      } catch (error) {
                        console.error("Error downloading recording:", error);
                      }
                    }}
                    title="Download Recording"
                  >
                    <Download className="h-3 w-3" />
                  </Button>
                </div>
              </>
            )
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export { CallLogsList };
export default CallLogsList;
